import { useCallback, useState } from 'react';
import { LazyQueryHookOptions } from '@apollo/client';

import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import {
  DictionaryDefinition,
  FormDefinition,
  FormExample,
  FormWordsTranslation,
  SOURCE_USER_DEFINED,
  VOCABULARY_SETTINGS,
} from './vocabulary-setting.constants';
import {
  useLoggingLazyQuery,
  useLoggingMutation,
} from 'lib/apollo/apollo-hooks';
import {
  SAVE_TO_DICTIONARY_BOOK,
  TRANSLATE_TO,
} from 'approot/dictionary/dictionary.graphql';
import type {
  TranslateToQuery,
  TranslateToQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import type {
  SaveToDictionaryBookMutation,
  SaveToDictionaryBookMutationVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { CONTENT_IMAGE, ImageType } from 'global/constants';

export const useRemoveDefinition = ({
  componentId,
  sectionId,
  definitions,
  onCompleted,
  onError,
}: {
  componentId: string;
  sectionId: string;
  definitions: DictionaryDefinition[];
  onCompleted?: () => void;
  onError?: () => void;
}) => {
  const { updateComponentProperties, loading } =
    useBatchComponentUpdateContext();

  const handleRemoveFromComponent = useCallback(
    (id: number) => {
      const remainingVocabulary = definitions.filter(v => v.id !== id);
      updateComponentProperties({
        input: {
          componentId,
          sectionId,
          componentData: {
            properties: {
              definitionIds: remainingVocabulary.map(v => v.id || 0),
            },
          },
        },
        typename: 'ActivitiesVocabularyComponent',
        isSaveImmediate: true,
        onCompleted: () => {
          onCompleted?.();
        },
      });
    },
    [
      componentId,
      sectionId,
      definitions,
      onCompleted,
      updateComponentProperties,
    ]
  );

  return {
    handleRemoveFromComponent,
    loading,
  };
};

export const useTranslateTo = () => {
  const { showError } = useSnackbar();

  const [error, setError] = useState<boolean>(false);

  const [translateTo, { loading }] = useLoggingLazyQuery<
    TranslateToQuery,
    TranslateToQueryVariables
  >(TRANSLATE_TO, {
    fetchPolicy: 'no-cache',
    onError: () => {
      setError(true);
      showError('There was an error translating the text. Please try again.');
    },
  });

  return {
    translateTo: (
      variables: Partial<
        LazyQueryHookOptions<TranslateToQuery, TranslateToQueryVariables>
      >
    ) => {
      setError(false);
      return translateTo(variables);
    },
    loading,
    error,
  };
};

export function transformExamplesAndDefinitions(
  examples: FormExample[],
  definitions: FormDefinition[]
) {
  return {
    transformedExamples: examples.map(item => ({
      example: item.example,
      exampleId: item.exampleId,
      language: item.language,
      image: item.image
        ? {
            key: item.image.key || '',
            alt: item.image.alt || '',
            fileName: item.image.fileName || '',
            imageType: CONTENT_IMAGE as ImageType,
            cropJson: item.image.cropData || undefined,
          }
        : undefined,
    })),
    transformedDefinitions: definitions.map(def => ({
      definition: def.description || '',
      year: def.year,
      language: def.language,
    })),
  };
}

export const useSaveToDictionaryBook = ({
  shouldUpdateComponent,
  componentId,
  sectionId,
  vocabularyIds,
  setWord,
  goTo,
}: {
  shouldUpdateComponent: boolean;
  componentId: string;
  sectionId: string;
  vocabularyIds: number[];
  hasSavedTranslations?: boolean;
  setWord?: (word: string | undefined) => void;
  goTo?: (id: number) => void;
}) => {
  const { showError } = useSnackbar();

  const { updateComponentProperties, loading: loadingUpdateComponent } =
    useBatchComponentUpdateContext();

  const [saveToDictionaryBook, { loading: loadingSaveToDictionary }] =
    useLoggingMutation<
      SaveToDictionaryBookMutation,
      SaveToDictionaryBookMutationVariables
    >(SAVE_TO_DICTIONARY_BOOK, {
      onCompleted: res => {
        if (shouldUpdateComponent) {
          updateComponentProperties({
            input: {
              componentId,
              sectionId,
              componentData: {
                properties: {
                  definitionIds: [
                    ...vocabularyIds,
                    res.saveToDictionaryBook.id,
                  ],
                },
              },
            },
            typename: 'VocabularyComponentProperties',
            isSaveImmediate: true,
            onCompleted: () => {
              setWord?.(undefined);
              goTo?.(VOCABULARY_SETTINGS);
            },
          });
        } else {
          goTo?.(VOCABULARY_SETTINGS);
        }
      },
      onError: () => showError('There was a problem saving to the dictionary'),
    });

  const saveToDictionary = useCallback(
    ({
      definition,
      wordsTranslation,
      examples,
      definitions,
      lexical,
      completionId,
    }: {
      definition: DictionaryDefinition;
      wordsTranslation: FormWordsTranslation[];
      examples: FormExample[];
      definitions: FormDefinition[];
      lexical: string;
      completionId?: string | null;
    }) => {
      const { transformedExamples, transformedDefinitions } =
        transformExamplesAndDefinitions(examples, definitions);

      return saveToDictionaryBook({
        variables: {
          input: {
            id: definition.id,
            word: definition.word.trim(),
            sourceDefinition: definition.sourceDefinition,
            wordsTranslation,
            source: shouldUpdateComponent ? SOURCE_USER_DEFINED : undefined,
            approved: true,
            lexical,
            completionId,
            examples: transformedExamples,
            aiDefinitions: transformedDefinitions,
          },
        },
      });
    },
    [shouldUpdateComponent, saveToDictionaryBook]
  );

  return {
    saveToDictionary,
    loadingSaveToDictionary,
    loadingUpdateComponent,
  };
};

export const useRemoveFromDictionaryBook = () => {
  const { showError } = useSnackbar();

  const [removeFromDictionaryBook, { loading: loadingRemoveFromDictionary }] =
    useLoggingMutation<
      SaveToDictionaryBookMutation,
      SaveToDictionaryBookMutationVariables
    >(SAVE_TO_DICTIONARY_BOOK, {
      onError: () =>
        showError(
          'There was a problem removing the translation from the dictionary'
        ),
    });

  const removeFromDictionary = useCallback(
    ({
      definition,
      wordsTranslation,
      examples,
      definitions,
      isManualAdd,
      lexical,
    }: {
      definition: DictionaryDefinition;
      wordsTranslation: FormWordsTranslation[];
      examples: FormExample[];
      definitions: FormDefinition[];
      isManualAdd: boolean;
      lexical: string;
    }) => {
      const { transformedExamples, transformedDefinitions } =
        transformExamplesAndDefinitions(examples, definitions);

      return removeFromDictionaryBook({
        variables: {
          input: {
            id: definition.id,
            word: definition.word,
            sourceDefinition: definition.sourceDefinition,
            wordsTranslation,
            source: isManualAdd ? SOURCE_USER_DEFINED : undefined,
            approved: true,
            lexical,
            examples: transformedExamples,
            aiDefinitions: transformedDefinitions,
          },
        },
      });
    },
    [removeFromDictionaryBook]
  );

  return {
    removeFromDictionary,
    loadingRemoveFromDictionary,
  };
};
